import React, { useState, useEffect } from "react";
import api from "../../api/axios.js";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./AddProductForm.css";

function EditProductForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  // Initial form state
  const initialFormState = {
    name_en: "",
    name_ar: "",
    description_en: "",
    description_ar: "",
    price: "",
    price_after_discount: "",
    stock: "",
    best_selling: "",
    category_id: "",
    image: null,
    old_image_url: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch product on mount
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        const product = data.data;
        setFormData({
          ...initialFormState,
          name_en: product.name_en ?? "",
          name_ar: product.name_ar ?? "",
          description_en: product.description_en ?? "",
          description_ar: product.description_ar ?? "",
          price: product.price ?? "",
          price_after_discount: product.price_after_discount ?? "",
          stock: product.stock ?? "",
          best_selling: product.best_selling ?? "",
          category_id: product.category_id ?? "",
          old_image_url: product.image ?? "",
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: t("alerts.error"),
          text: t("alerts.failed_fetch"),
          confirmButtonColor: "#3085d6",
        });
      }
    };
    fetchProduct();
  }, [id, t]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
    if (errors[name]) setErrors({ ...errors, [name]: "" });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name_en.trim()) newErrors.name_en = t("errors.required");
    if (!formData.name_ar.trim()) newErrors.name_ar = t("errors.required");
    if (!formData.description_en.trim())
      newErrors.description_en = t("errors.required");
    if (!formData.description_ar.trim())
      newErrors.description_ar = t("errors.required");

    if (!formData.price) newErrors.price = t("errors.required");
    else if (isNaN(formData.price)) newErrors.price = t("errors.number");

    if (!formData.price_after_discount) {
      newErrors.price_after_discount = t("errors.required");
    } else if (
      parseFloat(formData.price_after_discount) > parseFloat(formData.price)
    ) {
      newErrors.price_after_discount = t("errors.discount_invalid");
    }

    if (!formData.stock) newErrors.stock = t("errors.required");

    if (!formData.best_selling || isNaN(formData.best_selling)) {
      newErrors.best_selling = t("errors.number");
    }

    if (!formData.category_id) newErrors.category_id = t("errors.required");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // SweetAlert helpers
  const showErrorAlert = (message) => {
    Swal.fire({
      icon: "error",
      title: t("alerts.error"),
      text: message,
      confirmButtonColor: "#3085d6",
    });
  };

  const showSuccessAlert = () => {
    Swal.fire({
      icon: "success",
      title: t("alerts.success"),
      text: t("alerts.product_updated"),
      confirmButtonColor: "#3085d6",
    });
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showErrorAlert(t("alerts.fix_errors"));
      return;
    }

    setIsLoading(true);
    Swal.fire({
      title: t("alerts.processing"),
      html: t("alerts.please_wait"),
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    const data = new FormData();
    for (const key in formData) {
      if (
        key !== "old_image_url" &&
        formData[key] !== null &&
        formData[key] !== ""
      ) {
        data.append(key, formData[key]);
      }
    }

    try {
      await api.post(`/products/${id}?_method=PUT`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      Swal.close();
      showSuccessAlert();
      navigate("/products");
    } catch (err) {
      Swal.close();
      const errorMessage =
        err.response?.data?.message || err.message || t("alerts.failed_update");
      showErrorAlert(errorMessage);
      if (err.response?.data?.errors) setErrors(err.response.data.errors);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container my-3 py-2">
      <div className="add-product-form-container">
        <h2 className="mb-4">{t("edit_product.title")}</h2>
        <form onSubmit={handleSubmit} noValidate>
          <div className="row">
            {/* English Name */}
            <div className="col-md-6 mb-3">
              <label className="form-label">{t("add_product.name_en")} *</label>
              <input
                type="text"
                className={`form-control ${errors.name_en ? "is-invalid" : ""}`}
                name="name_en"
                value={formData.name_en}
                onChange={handleChange}
              />
              {errors.name_en && (
                <div className="invalid-feedback">{errors.name_en}</div>
              )}
            </div>

            {/* Arabic Name */}
            <div className="col-md-6 mb-3">
              <label className="form-label">{t("add_product.name_ar")} *</label>
              <input
                type="text"
                className={`form-control ${errors.name_ar ? "is-invalid" : ""}`}
                name="name_ar"
                value={formData.name_ar}
                onChange={handleChange}
              />
              {errors.name_ar && (
                <div className="invalid-feedback">{errors.name_ar}</div>
              )}
            </div>

            {/* English Description */}
            <div className="col-md-6 mb-3">
              <label className="form-label">
                {t("add_product.description_en")} *
              </label>
              <textarea
                className={`form-control ${
                  errors.description_en ? "is-invalid" : ""
                }`}
                name="description_en"
                value={formData.description_en}
                onChange={handleChange}
              ></textarea>
              {errors.description_en && (
                <div className="invalid-feedback">{errors.description_en}</div>
              )}
            </div>

            {/* Arabic Description */}
            <div className="col-md-6 mb-3">
              <label className="form-label">
                {t("add_product.description_ar")} *
              </label>
              <textarea
                className={`form-control ${
                  errors.description_ar ? "is-invalid" : ""
                }`}
                name="description_ar"
                value={formData.description_ar}
                onChange={handleChange}
              ></textarea>
              {errors.description_ar && (
                <div className="invalid-feedback">{errors.description_ar}</div>
              )}
            </div>

            {/* Price */}
            <div className="col-md-6 mb-3">
              <label className="form-label">{t("add_product.price")} *</label>
              <input
                type="number"
                className={`form-control ${errors.price ? "is-invalid" : ""}`}
                name="price"
                value={formData.price}
                onChange={handleChange}
                min="0"
                step="0.01"
              />
              {errors.price && (
                <div className="invalid-feedback">{errors.price}</div>
              )}
            </div>

            {/* Price After Discount */}
            <div className="col-md-6 mb-3">
              <label className="form-label">
                {t("add_product.price_after_discount")} *
              </label>
              <input
                type="number"
                className={`form-control ${
                  errors.price_after_discount ? "is-invalid" : ""
                }`}
                name="price_after_discount"
                value={formData.price_after_discount}
                onChange={handleChange}
                min="0"
                step="0.01"
              />
              {errors.price_after_discount && (
                <div className="invalid-feedback">
                  {errors.price_after_discount}
                </div>
              )}
            </div>

            {/* Stock */}
            <div className="col-md-6 mb-3">
              <label className="form-label">{t("add_product.stock")} *</label>
              <input
                type="number"
                className={`form-control ${errors.stock ? "is-invalid" : ""}`}
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                min="0"
              />
              {errors.stock && (
                <div className="invalid-feedback">{errors.stock}</div>
              )}
            </div>
            {/* best_selling */}
            <div className="col-md-6 mb-3">
              <label className="form-label">
                {t("add_product.best_selling")} *
              </label>
              <input
                type="number"
                className={`form-control ${
                  errors.best_selling ? "is-invalid" : ""
                }`}
                name="best_selling"
                value={formData.best_selling}
                onChange={handleChange}
                min="0"
              />
              {errors.best_selling && (
                <div className="invalid-feedback">{errors.best_selling}</div>
              )}
            </div>

            {/* Category Select */}
            <div className="col-md-6 mb-3">
              <label className="form-label">
                {t("add_product.category")} *
              </label>
              <select
                className={`form-control ${
                  errors.category_id ? "is-invalid" : ""
                }`}
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
              >
                <option value="">{t("add_product.select_category")}</option>
                <option value="1">{t("categories.makeup")}</option>
                <option value="2">{t("categories.skincare")}</option>
                <option value="3">{t("categories.haircare")}</option>
                <option value="4">{t("categories.bodycare")}</option>
                <option value="5">{t("categories.fragrance")}</option>
              </select>
              {errors.category_id && (
                <div className="invalid-feedback">{errors.category_id}</div>
              )}
            </div>

            {/* Image Upload */}
            <div className="col-12 mb-3">
              <label className="form-label">{t("add_product.image")}</label>
              <input
                type="file"
                className="form-control"
                name="image"
                onChange={handleChange}
                accept="image/*"
              />
            </div>

            {/* Image Preview */}
            {(formData.image || formData.old_image_url) && (
              <div className="col-12 mb-3">
                <img
                  src={
                    formData.image
                      ? URL.createObjectURL(formData.image)
                      : formData.old_image_url
                  }
                  alt="Preview"
                  className="img-thumbnail"
                  style={{ maxHeight: "200px" }}
                />
              </div>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                {t("edit_product.updating")}
              </>
            ) : (
              t("edit_product.update")
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default EditProductForm;
